import { readConversationSourceFacts } from "./conversationRead.js";

const UUID = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/;
const BATCH_SIZE = 128;
const MAX_IDS = 1000;
const WORKERS = 2;
// One budget for the whole enrichment, not per batch: the evidence is optional
// and the authorized rows are already ready, so a slow evidence store must not
// hold the list for several sequential batch deadlines.
export const LIST_SOURCE_FACTS_BUDGET_MS = 3000;

// Eight batches bound a 1000-row list. Two workers avoid a query per row, and
// each batch fails on its own so one bad batch leaves the others' evidence.
export async function enrichListSourceFacts(traces, { byTrace = false, timeRange, signal } = {}) {
  signal?.throwIfAborted();
  const keyOf = trace => String(byTrace ? trace.sessionId : trace.traceId).toLowerCase();
  const ids = [...new Set(traces.map(keyOf).filter(id => UUID.test(id)))].slice(0, MAX_IDS);
  const requested = new Set(ids);
  // Rows that are never requested (past the cap, or without a usable key) keep
  // no evidence state: no read failed for them, so a retry hint would mislead.
  for (const trace of traces) if (requested.has(keyOf(trace))) trace.clientEvidenceState = "unavailable";
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) batches.push(ids.slice(i, i + BATCH_SIZE));
  if (!batches.length) return;

  // Combined by hand rather than with AbortSignal.any/timeout, which older
  // browsers lack; a TypeError there would read as every batch failing.
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(new DOMException("Source evidence timed out.", "TimeoutError")), LIST_SOURCE_FACTS_BUDGET_MS);
  const forward = () => deadline.abort(signal.reason);
  signal?.addEventListener("abort", forward, { once: true });
  let next = 0;
  try {
    await Promise.all(Array.from({ length: Math.min(WORKERS, batches.length) }, async () => {
      while (next < batches.length && !deadline.signal.aborted) {
        const batch = batches[next++];
        try {
          const page = await readConversationSourceFacts({
            [byTrace ? "traceIds" : "sourceEventIds"]: batch, timeRange, signal: deadline.signal,
          });
          applyBatch(traces, keyOf, batch, page, byTrace);
        } catch {
          // Optional evidence cannot erase authorized list content. A failed or
          // unavailable read is distinct from a successful read with no facts.
          signal?.throwIfAborted();
        }
      }
    }));
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", forward);
  }
  signal?.throwIfAborted();
}

function applyBatch(traces, keyOf, batch, page, byTrace) {
  const grouped = new Map(batch.map(id => [id, []]));
  // A server that names the ids it truncated lets the other rows read as
  // complete. An older server sends only the batch flag, and then any row
  // in the batch may be incomplete: the row cap drops a busy session's older
  // observations, and dropped supplemental bundles leave the primary one in
  // place, so each row keeps what it got with the qualifier.
  const truncatedIds = page.truncated && Array.isArray(page.truncatedIds) && page.truncatedIds.length
    ? new Set(page.truncatedIds.map(id => String(id).toLowerCase())) : null;
  for (const entry of page.observations) {
    const key = byTrace ? entry.traceId : entry.sourceEventId;
    if (grouped.has(key)) grouped.get(key).push(entry);
  }
  for (const trace of traces) {
    const entries = grouped.get(keyOf(trace));
    if (!entries) continue;
    const truncated = Boolean(page.truncated) && (!truncatedIds || truncatedIds.has(keyOf(trace)));
    if (!entries.length && !truncated) {
      // A successful read with nothing for this row is no evidence, not a
      // failure; the row keeps its non-evidence harness and model labels.
      trace.clientEvidenceState = "empty";
      continue;
    }
    trace.clientAttributions = entries;
    trace.clientAttributionsTruncated = truncated;
    trace.clientEvidenceState = "loaded";
  }
}
